import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

export interface WorkHistoryEntry {
  id: string;
  company: string;
  title: string;
  start_date: string | null;
  end_date: string | null;
  description: string | null;
  skills_used: string[];
  is_current: boolean;
  sort_order: number;
}

export interface EducationEntry {
  id: string;
  institution: string;
  degree: string | null;
  field_of_study: string | null;
  start_date: string | null;
  end_date: string | null;
  description: string | null;
}

export interface CertificationEntry {
  id: string;
  name: string;
  issuing_organization: string | null;
  issue_date: string | null;
  expiration_date: string | null;
  credential_id: string | null;
}

export function useCareerHistory() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const workHistory = useQuery({
    queryKey: ['work-history', user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('work_history')
        .select('*')
        .eq('user_id', user.id)
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return (data || []) as WorkHistoryEntry[];
    },
    enabled: !!user,
  });

  const education = useQuery({
    queryKey: ['education', user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('education')
        .select('*')
        .eq('user_id', user.id)
        .order('start_date', { ascending: false });
      if (error) throw error;
      return (data || []) as EducationEntry[];
    },
    enabled: !!user,
  });

  const certifications = useQuery({
    queryKey: ['certifications', user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('certifications')
        .select('*')
        .eq('user_id', user.id)
        .order('issue_date', { ascending: false });
      if (error) throw error;
      return (data || []) as CertificationEntry[];
    },
    enabled: !!user,
  });

  const saveWorkHistory = useMutation({
    mutationFn: async (entries: Omit<WorkHistoryEntry, 'id'>[]) => {
      if (!user) throw new Error('Not authenticated');
      const { error } = await supabase
        .from('work_history')
        .insert(entries.map((e, i) => ({ ...e, user_id: user.id, sort_order: i })));
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['work-history'] }),
  });

  const saveEducation = useMutation({
    mutationFn: async (entries: Omit<EducationEntry, 'id'>[]) => {
      if (!user) throw new Error('Not authenticated');
      const { error } = await supabase
        .from('education')
        .insert(entries.map(e => ({ ...e, user_id: user.id })));
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['education'] }),
  });

  const saveCertifications = useMutation({
    mutationFn: async (entries: Omit<CertificationEntry, 'id'>[]) => {
      if (!user) throw new Error('Not authenticated');
      const { error } = await supabase
        .from('certifications')
        .insert(entries.map(e => ({ ...e, user_id: user.id })));
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['certifications'] }),
  });

  return {
    workHistory: workHistory.data || [],
    education: education.data || [],
    certifications: certifications.data || [],
    isLoading: workHistory.isLoading || education.isLoading || certifications.isLoading,
    saveWorkHistory,
    saveEducation,
    saveCertifications,
  };
}
